import { conversationTarget } from "./conversationIdentity.mjs";

const hasId = value => value != null && String(value) !== "";
const leadKey = value => String(value ?? "").replace(/^aurora_/, "");
const positiveId = (...values) => values.map(Number).find(value => Number.isFinite(value) && value > 0) || null;

// Display/navigation only: never repair a link, look up another origin, or write.
export function resolveReminderCard(reminder, {
  clients = [], leads = [], prospeccao = [], currentUser,
  leadConsultantId = lead => lead.consultantId ?? lead.consultorAtual ?? lead.assignedConsultantId,
  consultantStoreId = () => null,
} = {}) {
  if (!reminder || !currentUser) return null;
  let moduleId, candidates;
  if (hasId(reminder.leadId)) {
    moduleId = "leads";
    candidates = leads.filter(lead => [lead.id, lead._rawPhone].some(id => leadKey(id) === leadKey(reminder.leadId)));
    // Older physical-client reminders used leadId for the exact client key.
    // Match only that key, never the name/phone of another origin.
    if (!String(reminder.leadId).startsWith("aurora_")) {
      const exactClients = clients.filter(client => String(client.id) === String(reminder.leadId));
      if (exactClients.length) { moduleId = "kanban"; candidates = exactClients; }
    }
  } else if (hasId(reminder.prospId)) {
    moduleId = "prospeccao";
    candidates = prospeccao.filter(prospect => String(prospect.id) === String(reminder.prospId));
  } else if (hasId(reminder.clientId)) {
    moduleId = "kanban";
    candidates = clients.filter(client => String(client.id) === String(reminder.clientId));
  } else if (reminder.source === "commercial_tracking") {
    // Virtual reminders have no legacy name-based linkage. Only a unique
    // store+phone contact is safe, and the same role checks still apply.
    const phone = reminder.phone || reminder.telefone;
    if (!phone || !reminder.storeId) return null;
    const target = conversationTarget("kanban", reminder.storeId, phone);
    if (!target) return null;
    const linked = [
      ...clients.map(record => ({ field:"clientId", record })),
      ...leads.map(record => ({ field:"leadId", record })),
      ...prospeccao.map(record => ({ field:"prospId", record })),
    ].filter(({ record }) => conversationTarget("kanban", reminder.storeId, record.phone || record.telefone)?.phone === target.phone)
      .map(({ field, record }) => resolveReminderCard({ ...reminder, [field]:record.id }, {
        clients, leads, prospeccao, currentUser, leadConsultantId, consultantStoreId,
      })).filter(Boolean);
    return linked.length === 1 ? linked[0] : null;
  } else {
    // Preserve the historical in-person client-name lookup, only when no ID exists.
    if (!reminder.clientName) return null;
    moduleId = "kanban";
    const norm = value => (value || "").trim().toLowerCase();
    const client = clients.find(record => norm(record.name) === norm(reminder.clientName));
    candidates = client ? [client] : [];
  }

  const matches = candidates.flatMap(record => {
    const ownerId = positiveId(moduleId === "leads" ? leadConsultantId(record) : record.consultantId);
    const storeId = moduleId === "leads"
      ? positiveId(record.lojaAtual, record.responsibleStoreId, record.storeId, consultantStoreId(ownerId))
      : positiveId(record.storeId, consultantStoreId(ownerId));
    if (!storeId) return [];
    if (hasId(reminder.storeId) && Number(reminder.storeId) !== storeId) return [];
    const allowed = ["admin","superadmin"].includes(currentUser.role)
      || (currentUser.role === "manager" && Number(currentUser.storeId) === storeId)
      || (currentUser.role === "consultant" && ownerId != null && Number(currentUser.id) === ownerId);
    if (!allowed) return [];
    const phone = record.telefone || record.phone || "";
    const target = {
      ...(conversationTarget(moduleId, storeId, phone) || { moduleId, storeId, phone }),
      recordId:String(record.id),
      reminderCard:true,
    };
    return [{ moduleId, record, target }];
  });
  return matches.length === 1 ? matches[0] : null;
}